import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { formatCurrency, formatPercent } from './helpers';

/**
 * pdfStyles - Hoja de diseño estricta para la compilación de la liquidación oficial.
 * Diseñada para simular un papel de comprobante corporativo minimalista.
 */
const pdfStyles = StyleSheet.create({
    page: { 
        padding: 40, 
        fontSize: 8, 
        fontFamily: 'Helvetica', 
        color: '#1e293b' 
    },
    header: { 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        borderBottomWidth: 2,
        borderBottomColor: '#0f172a', 
        paddingBottom: 15, 
        marginBottom: 20 
    },
    title: { 
        fontSize: 18, 
        fontWeight: 'bold', 
        color: '#1e3a8a',
        letterSpacing: -0.5
    },
    subtitle: { 
        fontSize: 7, 
        color: '#64748b', 
        textTransform: 'uppercase', 
        marginTop: 2,
        letterSpacing: 1
    },
    metaBlock: { 
        marginTop: 6 
    },
    metaRow: { 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        marginBottom: 3 
    },
    metaLabel: {
        color: '#475569',
        fontSize: 8
    },
    metaVal: {
        fontSize: 8,
        fontWeight: 'bold',
        color: '#0f172a'
    },
    companySection: { 
        marginBottom: 15,
        break: 'avoid'
    },
    companyTitle: { 
        fontSize: 10, 
        fontWeight: 'bold', 
        color: '#1e3a8a', 
        borderBottomWidth: 1,
        borderBottomColor: '#cbd5e1', 
        paddingBottom: 3, 
        marginBottom: 4,
        textTransform: 'uppercase'
    },
    originTitle: { 
        fontSize: 7, 
        color: '#64748b', 
        textTransform: 'uppercase',
        marginBottom: 4,
        letterSpacing: 0.5
    },
    tableHeader: { 
        flexDirection: 'row', 
        backgroundColor: '#f8fafc', 
        borderBottomWidth: 1,
        borderBottomColor: '#0f172a', 
        padding: 4, 
        fontWeight: 'bold' 
    },
    tableRow: { 
        flexDirection: 'row', 
        borderBottomWidth: 1,
        borderBottomColor: '#e2e8f0', 
        padding: 4 
    },
    colClient: { width: '22%' },
    colDoc: { width: '13%' },
    colDocNum: { width: '13%' },
    colRamo: { width: '7%', textAlign: 'center' },
    colBase: { width: '13%', textAlign: 'right' },
    colPct: { width: '8%', textAlign: 'center' },
    colBruto: { width: '10%', textAlign: 'right' },
    colDesc: { width: '6%', textAlign: 'center' },
    colNeto: { width: '10%', textAlign: 'right', fontWeight: 'bold' },
    
    subtotalRow: { 
        flexDirection: 'row', 
        justifyContent: 'flex-end', 
        paddingTop: 5, 
        paddingBottom: 5, 
        borderTopWidth: 1,
        borderTopColor: '#0f172a', 
        marginTop: 2 
    },
    subtotalLabel: { 
        width: '90%', 
        textAlign: 'right', 
        fontSize: 7, 
        color: '#475569', 
        textTransform: 'uppercase', 
        paddingRight: 8 
    },
    subtotalVal: { 
        width: '10%', 
        textAlign: 'right', 
        fontWeight: 'bold', 
        color: '#0f172a' 
    },
    grandTotalContainer: { 
        marginTop: 25, 
        flexDirection: 'row', 
        justifyContent: 'flex-end',
        break: 'avoid'
    },
    grandTotalBox: { 
        backgroundColor: '#f1f5f9', 
        padding: 10, 
        borderRadius: 6, 
        borderWidth: 1, 
        borderColor: '#e2e8f0', 
        width: 200, 
        textAlign: 'right' 
    },
    grandTotalLabel: { 
        fontSize: 7, 
        color: '#475569', 
        textTransform: 'uppercase', 
        marginBottom: 2 
    },
    grandTotalVal: { 
        fontSize: 14, 
        fontWeight: 'bold', 
        color: '#1e3a8a' 
    },
    footerSignatures: { 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        marginTop: 60, 
        paddingLeft: 10, 
        paddingRight: 10,
        break: 'avoid'
    },
    signatureLine: { 
        borderTopWidth: 1,
        borderTopColor: '#0f172a', 
        width: 140, 
        textAlign: 'center', 
        paddingTop: 4, 
        fontSize: 7, 
        color: '#64748b',
        textTransform: 'uppercase'
    }
});

/**
 * LiquidacionPDFDocument - Componente de renderizado de bytes del PDF.
 * Proyecta la tabla, agrupamientos de aseguradoras y subtotales de comisiones.
 */
const LiquidacionPDF = ({ period, modo, entidad, totals, groupedData }) => (
    <Document>
        <Page size="A4" style={pdfStyles.page}>
            {/* ENCABEZADO */}
            <View style={pdfStyles.header}>
                <View>
                    <Text style={pdfStyles.title}>IB BROKERS</Text>
                    <Text style={pdfStyles.subtitle}>Asesores en Seguros • Liquidaciones Oficiales</Text>
                    <View style={pdfStyles.metaBlock}>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#1e3a8a' }}>
                            Liquidación para: {entidad}
                        </Text>
                        <Text style={{ fontSize: 7, color: '#475569', marginTop: 1 }}>
                            Estatus: Comprobante oficial de {modo}
                        </Text>
                    </View>
                </View>
                <View style={{ textAlign: 'right' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#0f172a' }}>DETALLE DE LIQUIDACIÓN</Text>
                    <Text style={{ fontSize: 8, marginTop: 4 }}>Periodo: {period}</Text>
                    <Text style={{ fontSize: 7, color: '#64748b', marginTop: 10 }}>Generado el {new Date().toLocaleDateString('es-AR')}</Text>
                </View>
            </View>

            {/* TABLA POR COMPAÑÍAS */}
            {Object.entries(groupedData).map(([cia, formas]) => (
                <View key={cia} style={pdfStyles.companySection}>
                    <Text style={pdfStyles.companyTitle}>Aseguradora: {cia}</Text>
                    
                    {Object.entries(formas).map(([forma, items]) => {
                        const subTotal = items.reduce((acc, curr) => acc + curr.res_neto, 0);
                        return (
                            <View key={forma} style={{ marginBottom: 10 }}>
                                <Text style={pdfStyles.originTitle}>Origen: {forma}</Text>
                                
                                {/* CABECERA TABLA */}
                                <View style={pdfStyles.tableHeader}>
                                    <Text style={[pdfStyles.colClient, { fontWeight: 'bold' }]}>Cliente / Razón Social</Text>
                                    <Text style={[pdfStyles.colDoc, { fontWeight: 'bold' }]}>Doc. / Póliza</Text>
                                    <Text style={[pdfStyles.colDocNum, { fontWeight: 'bold' }]}>DNI / CUIT</Text>
                                    <Text style={[pdfStyles.colRamo, { fontWeight: 'bold', textAlign: 'center' }]}>Ramo</Text>
                                    <Text style={[pdfStyles.colBase, { fontWeight: 'bold', textAlign: 'right' }]}>Base $</Text>
                                    <Text style={[pdfStyles.colPct, { fontWeight: 'bold', textAlign: 'center' }]}>% Part.</Text>
                                    <Text style={[pdfStyles.colBruto, { fontWeight: 'bold', textAlign: 'right' }]}>Bruta $</Text>
                                    <Text style={[pdfStyles.colDesc, { fontWeight: 'bold', textAlign: 'center' }]}>Desc %</Text>
                                    <Text style={[pdfStyles.colNeto, { fontWeight: 'bold', textAlign: 'right' }]}>Neto $</Text>
                                </View>

                                {/* FILAS TABLA */}
                                {items.map((row, i) => (
                                    <View key={i} style={pdfStyles.tableRow}>
                                        <Text style={pdfStyles.colClient}>{`${row.clienteNombre} ${row.clienteApellido || ''}`.toUpperCase()}</Text>
                                        <Text style={pdfStyles.colDoc}>{row.nroDocumento}</Text>
                                        <Text style={pdfStyles.colDocNum}>{row.clienteCuit || row.clienteDni || 'S/D'}</Text>
                                        <Text style={[pdfStyles.colRamo, { textAlign: 'center' }]}>{row.tipoRamoLabel}</Text>
                                        <Text style={[pdfStyles.colBase, { textAlign: 'right' }]}>{formatCurrency(row.base)}</Text>
                                        <Text style={[pdfStyles.colPct, { textAlign: 'center' }]}>{formatPercent(row.res_pct)}</Text>
                                        <Text style={[pdfStyles.colBruto, { textAlign: 'right' }]}>{formatCurrency(row.res_bruto)}</Text>
                                        <Text style={[pdfStyles.colDesc, { textAlign: 'center' }]}>{row.res_desc > 0 ? `-${row.res_desc}%` : '-'}</Text>
                                        <Text style={[pdfStyles.colNeto, { textAlign: 'right' }]}>{formatCurrency(row.res_neto)}</Text>
                                    </View>
                                ))}

                                {/* SUBTOTAL */}
                                <View style={pdfStyles.subtotalRow}>
                                    <Text style={pdfStyles.subtotalLabel}>Subtotal {cia} ({forma}):</Text>
                                    <Text style={pdfStyles.subtotalVal}>{formatCurrency(subTotal)}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>
            ))}

            {/* TOTAL FINAL */}
            <View style={pdfStyles.grandTotalContainer}>
                <View style={pdfStyles.grandTotalBox}>
                    <Text style={pdfStyles.grandTotalLabel}>Monto Neto Final a Liquidar</Text>
                    <Text style={pdfStyles.grandTotalVal}>{formatCurrency(totals.netoTotal)}</Text>
                </View>
            </View>

            {/* FIRMAS DE CONTROL */}
            <View style={pdfStyles.footerSignatures}>
                <View>
                    <View style={pdfStyles.signatureLine}>
                        <Text>Firma del Interesado</Text>
                    </View>
                </View>
                <View>
                    <View style={pdfStyles.signatureLine}>
                        <Text>IB BROKERS S.A.</Text>
                    </View>
                </View>
            </View>
        </Page>
    </Document>
);

export default LiquidacionPDF;